import { createContext, useState, useEffect, ReactNode, useContext, useRef, useCallback } from 'react';
import axios from '../axios-config';

export interface OrganizationType {
    organizationId: string;
    organizationName: string;
    organizationTypeId: number;
    pointsAvailable: number;
    pointsAwarded: number;
    deActivatedOn: Date; //TODO validate
    organizationUsers: any[]; // TODO: Define type
}

export interface OrganizationWithTotals extends OrganizationType {
    totalStudents: number;
    totalTeachers: number;
}

interface OrganizationsContextValue {
    organizations: OrganizationType[];
    fetchOrganizations: () => Promise<void>;
    schools: OrganizationWithTotals[];
    teachers: any[];
    students: any[];
}

export const OrganizationsContext = createContext<OrganizationsContextValue>({
    organizations: [],
    fetchOrganizations: async () => {},
    schools: [],
    teachers: [],
    students: []
});

export const OrganizationsProvider = ({ children }: { children: ReactNode }) => {
    const [Organizations, setOrganizations] = useState<OrganizationType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const isInitializedRef = useRef(false);

    const fetchOrganizations = useCallback(async () => {
        if (isLoading || isInitializedRef.current) {
            console.log("Skipping fetchOrganizations: Already loading or initialized");
            return;
        }
        console.log("Fetching organization types...");
        setIsLoading(true);
        try {
            const { data } = await axios.get('/api/organizations');
            setOrganizations(Array.isArray(data) ? data : (data.organizations ?? []));
            isInitializedRef.current = true;
        } catch (err: any) {
            console.error('Error fetching organization types:', err.message);
            setOrganizations([]);
            isInitializedRef.current = true;
        } finally {
            setIsLoading(false);
        }
    }, [isLoading]);

    const schools: OrganizationWithTotals[] = Organizations
        .filter(org => org.organizationTypeId === 1 || org.organizationTypeId === 2)
        .map(org => ({
            ...org,
            totalStudents: org.organizationUsers ? org.organizationUsers.filter((user: any) => user.acornUser.mainRole === "STUDENT").length : 0,
            totalTeachers: org.organizationUsers ? org.organizationUsers.filter((user: any) => user.acornUser.mainRole.includes("TEACHER")).length : 0
        }));

    const teachers = Organizations.flatMap(org =>
        org.organizationUsers ? org.organizationUsers.filter((user: any) => user.acornUser.mainRole.includes("TEACHER")) : []
    );
    const students = Organizations.flatMap(org =>
        org.organizationUsers ? org.organizationUsers.filter((user: any) => user.acornUser.mainRole === "STUDENT") : []
    );

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]); // Run only once on mount

    return (
        <OrganizationsContext.Provider value={{ organizations: Organizations, fetchOrganizations, schools, teachers, students }}>
            {children}
        </OrganizationsContext.Provider>
    );
};

export const useOrganizations = () => useContext(OrganizationsContext);
